import { Box, CircularProgress, InputAdornment, TextField } from "@mui/material";
import { useState } from "react";
import tripService from "services/tripService";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const LABELS: Record<string, string> = {
  tripNumber: "Trip Number",
  controlNumber: "Control Number",
  shortKey: "Short Key",
};

const TripNumberSearch = () => {
  const navigate = useNavigate();
  const [tripNumber, setTripNumber] = useState("");
  const [controlNumber, setControlNumber] = useState("");
  const [shortKey, setShortKey] = useState("");
  // null when idle, otherwise the identifierType currently in-flight. Used to
  // gate the inputs and drive the per-field spinner.
  const [searching, setSearching] = useState<string | null>(null);

  async function search(rawValue: string, identifierType: string) {
    const value = (rawValue ?? "").trim();
    if (!value) return;
    setSearching(identifierType);
    try {
      // Service is typed as Promise<string[]> but the API actually returns a
      // scalar trip id (number 0 = not found, or the matching id). Cast through
      // any so the runtime checks below aren't second-guessed by the compiler.
      const result: any = await tripService.searchTripIdentifier(value, identifierType);
      // API returns 0 (not the string "0") when nothing matches. Treat 0,
      // null, undefined, and "" identically: "no trip found", with feedback.
      if (!result || result === 0 || result === "0") {
        toast.warning(`No trip found for ${LABELS[identifierType] ?? identifierType} "${value}"`);
        return;
      }
      setTripNumber("");
      setControlNumber("");
      setShortKey("");
      navigate(`/trips/${result}`);
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = status ? `HTTP ${status}` : (err?.message || "request error");
      toast.error(`Search failed for ${LABELS[identifierType] ?? identifierType} "${value}" — ${detail}`);
    } finally {
      setSearching(null);
    }
  }

  /**
   * Submitting on Enter, including on a phone.
   *
   * This used to be an onKeyDown checking `e.key === "Enter"`, which works on a desktop keyboard and
   * fails on a phone. Android's on-screen keyboard routes text through an IME, and while the IME is
   * composing, keydown reports `keyCode 229` / `key: "Unidentified"` rather than the real key -- so
   * the Enter branch never ran and the key did nothing. There was also no reason for the keyboard to
   * offer a "Go" key at all, because nothing on the page was submittable.
   *
   * A real form fixes both. Native implicit submission does not go through keydown, so the IME
   * cannot swallow it, and mobile browsers label the key once there is something to submit.
   *
   * ONE FORM PER FIELD, deliberately. Implicit submission only happens when a form has a single
   * text input and no submit button -- wrapping all three in one form would submit nothing, and
   * would also lose track of which of the three the user actually typed in.
   *
   * `display: contents` keeps the form out of the navbar's flex row, so each TextField stays the
   * flex item it was and the layout is unchanged.
   */
  const searchForm = (identifierType: string, value: string, field: JSX.Element) => (
    <Box
      component="form"
      // `noValidate` matters: the Trip Number input carries pattern="[0-9]*" as a keyboard hint, and
      // inside a form that pattern would start gating submission and popping a native validation
      // bubble. Off, it stays a hint, exactly as it behaved before there was a form.
      noValidate
      onSubmit={(e: React.FormEvent) => {
        e.preventDefault();
        search(value, identifierType);
      }}
      sx={{ display: "contents" }}
    >
      {field}
    </Box>
  );

  const endAdornment = (field: string) =>
    searching === field ? (
      <InputAdornment position="end">
        <CircularProgress size={16} />
      </InputAdornment>
    ) : undefined;

  const disabledForOthers = (field: string) => searching !== null && searching !== field;

  return (
    <>
      {searchForm(
        "tripNumber",
        tripNumber,
        <TextField
          sx={{ mt: 2, ml: 1 }}
          name="tripNumber"
          label="Trip Number"
          value={tripNumber}
          disabled={disabledForOthers("tripNumber")}
          onChange={(e) => setTripNumber(e.target.value)}
          // Trip Number is always the decimal string of Trip.TripID (set by the
          // dbo.Insert_Trip trigger), so it is digits-only. inputMode surfaces the
          // numeric keypad on mobile; pattern is the legacy iOS keypad hint. Neither
          // restricts typing or paste, and the form above is noValidate, so pattern
          // still does not gate submission. Control Number ("6752724-6/19") and
          // Short Key (char(6) from BNSF) are NOT digits-only and are left as text.
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*", enterKeyHint: "search" }}
          InputProps={{ endAdornment: endAdornment("tripNumber") }}
        />
      )}
      {searchForm(
        "controlNumber",
        controlNumber,
        <TextField
          sx={{ mt: 2, ml: 1 }}
          name="controlNumber"
          label="Control Number"
          value={controlNumber}
          disabled={disabledForOthers("controlNumber")}
          onChange={(e) => setControlNumber(e.target.value)}
          inputProps={{ enterKeyHint: "search" }}
          InputProps={{ endAdornment: endAdornment("controlNumber") }}
        />
      )}
      {searchForm(
        "shortKey",
        shortKey,
        <TextField
          sx={{ mt: 2, ml: 1 }}
          name="shortKey"
          label="Short Key"
          value={shortKey}
          disabled={disabledForOthers("shortKey")}
          onChange={(e) => setShortKey(e.target.value)}
          inputProps={{ enterKeyHint: "search" }}
          InputProps={{ endAdornment: endAdornment("shortKey") }}
        />
      )}
    </>
  );
};

export default TripNumberSearch;
